// -----------------------------------------------------------------------------
// Left rail of the chat page — channels list, DMs list, search, and the
// "team chat" header pill (online count + global unread badge).
// -----------------------------------------------------------------------------

import React from "react";
import {
  BellSlashIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { fullNameFor } from "../../utils/chatHelpers";
import { IChatChannel, IChatUser } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";
import { formatRelative } from "./chatTime";

interface ChannelSidebarProps {
  me: IChatUser | null;
  usersById: Map<string, IChatUser>;
  teamChannels: IChatChannel[];
  dmChannels: IChatChannel[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (value: string) => void;
  unreadFor: (id: string) => number;
  totalUnread: number;
  onNewChannel: () => void;
  onNewDm: () => void;
}

export default function ChannelSidebar({
  me,
  usersById,
  teamChannels,
  dmChannels,
  selectedId,
  onSelect,
  search,
  onSearch,
  unreadFor,
  totalUnread,
  onNewChannel,
  onNewDm,
}: ChannelSidebarProps) {
  const onlineCount = Array.from(usersById.values()).filter(
    (u) => u.presence === "online"
  ).length;

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-gray-200 bg-gray-50">
      <div className="border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              Team chat
              {totalUnread > 0 ? (
                <span
                  className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                  title={`${totalUnread} unread message${totalUnread === 1 ? "" : "s"} (excluding muted channels)`}
                >
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              ) : null}
            </p>
            <p className="text-xs text-gray-500">
              {onlineCount} online · {usersById.size} teammates
            </p>
          </div>
          {me ? <ChatAvatar user={me} size="sm" showPresence /> : null}
        </div>
        <label className="mt-3 flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search channels and people"
            className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearch("")}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          ) : null}
        </label>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        <SidebarSection
          title="Channels"
          actionLabel="New thread"
          onAction={onNewChannel}
        >
          {teamChannels.length === 0 ? (
            <EmptyHint>No channels match.</EmptyHint>
          ) : (
            teamChannels.map((c) => (
              <SidebarRow
                key={c.id}
                active={selectedId === c.id}
                onClick={() => onSelect(c.id)}
                leading={<HashtagIcon className="h-4 w-4 text-gray-500" />}
                title={c.name}
                subtitle={c.description}
                unread={unreadFor(c.id)}
                muted={!!c.muted}
                trailingText={formatRelative(c.lastMessageAt)}
              />
            ))
          )}
        </SidebarSection>

        <SidebarSection
          title="Direct messages"
          actionLabel="New chat"
          onAction={onNewDm}
        >
          {dmChannels.length === 0 ? (
            <EmptyHint>No direct messages yet.</EmptyHint>
          ) : (
            dmChannels.map((c) => {
              const otherId = c.memberIds.find((id) => id !== me?.id);
              const other = otherId ? usersById.get(otherId) : undefined;
              return (
                <SidebarRow
                  key={c.id}
                  active={selectedId === c.id}
                  onClick={() => onSelect(c.id)}
                  leading={
                    other ? (
                      <ChatAvatar
                        user={other}
                        size="xs"
                        showPresence
                        tooltip={false}
                      />
                    ) : (
                      <UserPlusIcon className="h-4 w-4 text-gray-500" />
                    )
                  }
                  title={other ? fullNameFor(other) : "Direct message"}
                  subtitle={other?.role}
                  unread={unreadFor(c.id)}
                  muted={!!c.muted}
                  trailingText={formatRelative(c.lastMessageAt)}
                />
              );
            })
          )}
        </SidebarSection>
      </div>
    </aside>
  );
}

interface SidebarSectionProps {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
}

function SidebarSection({
  title,
  actionLabel,
  onAction,
  children,
}: SidebarSectionProps) {
  return (
    <div className="px-2 py-2">
      <div className="flex items-center justify-between px-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </span>
        {onAction && actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 shadow-sm hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          >
            <PlusIcon className="h-3 w-3" />
            <span>{actionLabel}</span>
          </button>
        ) : null}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <li className="px-3 py-2 text-xs text-gray-400">{children}</li>;
}

interface SidebarRowProps {
  active: boolean;
  onClick: () => void;
  leading: React.ReactNode;
  title: string;
  subtitle?: string;
  unread: number;
  muted?: boolean;
  trailingText?: string;
}

function SidebarRow({
  active,
  onClick,
  leading,
  title,
  subtitle,
  unread,
  muted,
  trailingText,
}: SidebarRowProps) {
  const hasUnread = unread > 0;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={classNames(
          "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
          active
            ? "bg-primary-50 text-primary-900"
            : "text-gray-700 hover:bg-white"
        )}
      >
        <span className="flex h-5 w-5 items-center justify-center">
          {leading}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1">
              <span
                className={classNames(
                  "truncate",
                  hasUnread && !active && !muted
                    ? "font-semibold text-gray-900"
                    : "",
                  muted ? "text-gray-500" : ""
                )}
              >
                {title}
              </span>
              {muted ? (
                <BellSlashIcon
                  className="h-3.5 w-3.5 shrink-0 text-gray-400"
                  title="Muted"
                />
              ) : null}
            </span>
            {trailingText ? (
              <span className="shrink-0 text-[10px] text-gray-400">
                {trailingText}
              </span>
            ) : null}
          </span>
          {subtitle ? (
            <span className="mt-0.5 block truncate text-xs text-gray-500">
              {subtitle}
            </span>
          ) : null}
        </span>
        {hasUnread ? (
          <span
            className={classNames(
              "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none",
              muted ? "bg-gray-300 text-gray-700" : "bg-primary-600 text-white"
            )}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
    </li>
  );
}
